import { create } from 'zustand';
import { TuiDocument } from '../core/Document';
import { Grid } from '../core/Grid';
import { CellPatch, History, HistoryState, makeCommand } from '../core/History';
import { Layer } from '../core/Layer';
import { Selection } from '../core/Selection';
import {
  Cell,
  Color,
  ColorMode,
  EMPTY_CELL,
  Point,
  Rect,
  TRANSPARENT,
  rectFromPoints,
} from '../core/types';
import { DEFAULT_BRUSH, getTool } from '../tools/registry';
import { moveDelta } from '../tools/drawTools';
import { BrushState, CellWrite, ToolEnv, ToolId, ToolStroke } from '../tools/types';
import { DEFAULT_RENDER_OPTIONS, RenderOptions } from '../render/CanvasRenderer';
import { parseAnsi } from '../ansi/Parser';
import { encodeAnsi, EncodeOptions } from '../ansi/Encoder';
import { SerializedComponent, encodeComponentRuns, fromJson, toJson } from '../project/Serializer';
import type { VariableDef, VariableValues } from '../runtime/Variables';
import { VariableRegistry } from '../runtime/Variables';
import {
  DEFAULT_ANIMATION,
  GradientAnimation,
  offsetCycle,
  sampleGradient,
} from '../tools/gradient';
import {
  Animation,
  newAnimation,
  newFrame,
  playbackOrder,
  pruneFrames,
} from '../animation/model';

/**
 * Editor state.
 *
 * Documents, history and selections are long-lived mutable objects; the store
 * holds references to them and bumps `revision` when something changes, so
 * React re-renders panels without ever copying a 45 000-cell grid.
 */

export interface EditorTab {
  id: string;
  doc: TuiDocument;
  history: History;
  selection: Selection;
  fileName: string;
  dirty: boolean;
  components: SerializedComponent[];
  animations: Animation[];
  createdAt: string;
}

export interface StatusMessage {
  id: number;
  text: string;
  kind: 'info' | 'warn' | 'error' | 'success';
}

/**
 * What a batch of cell writes actually did.
 *
 * `nothing-to-write` means the action produced no candidate cells at all;
 * `no-change` means it produced some, but they already looked like that.
 */
type CommitResult = 'ok' | 'locked' | 'hidden' | 'nothing-to-write' | 'no-change';

interface DragState {
  active: boolean;
  tool: ToolId;
  stroke: ToolStroke;
}

export interface EditorState {
  tabs: EditorTab[];
  activeTabId: string;
  revision: number;
  historyState: HistoryState;

  tool: ToolId;
  previousTool: ToolId | null;
  brush: BrushState;
  renderOptions: RenderOptions;

  drag: DragState | null;
  preview: Map<number, Cell> | null;
  marquee: Rect | null;
  cursor: Point | null;
  caret: Point | null;

  clipboard: Grid | null;
  recentColors: Color[];
  messages: StatusMessage[];
  showVariablePreview: boolean;

  activeAnimationId: string | null;
  playing: boolean;
  currentFrameIndex: number;

  /* actions */
  active(): EditorTab;
  bump(): void;
  notify(text: string, kind?: StatusMessage['kind']): void;
  dismiss(id: number): void;

  setTool(tool: ToolId): void;
  setBrush(patch: Partial<BrushState>): void;
  setRenderOptions(patch: Partial<RenderOptions>): void;
  pushRecentColor(color: Color): void;

  beginStroke(point: Point, frac: Point, mods: { shift: boolean; alt: boolean; ctrl: boolean }, button: number): void;
  updateStroke(point: Point, frac: Point, mods: { shift: boolean; alt: boolean; ctrl: boolean }): void;
  endStroke(): void;
  cancelStroke(): void;
  setCursor(point: Point | null): void;

  undo(): void;
  redo(): void;

  addLayer(): void;
  removeLayer(id: string): void;
  duplicateLayer(id: string): void;
  renameLayer(id: string, name: string): void;
  toggleLayerVisible(id: string): void;
  toggleLayerLocked(id: string): void;
  moveLayer(id: string, toIndex: number): void;
  setActiveLayer(id: string): void;
  mergeLayerDown(id: string): void;

  selectAll(): void;
  selectNone(): void;
  invertSelection(): void;
  selectByColor(color: Color, channel: 'fg' | 'bg'): void;
  selectByChar(char: string): void;
  setSelection(selection: Selection): void;

  copySelection(): void;
  cutSelection(): void;
  paste(at?: Point): void;
  deleteSelection(): void;
  flipSelection(axis: 'h' | 'v'): void;
  replaceInSelection(match: Partial<Cell>, replacement: Partial<Cell>): number;
  nudgeSelection(dx: number, dy: number): void;
  /** Paints the brush into every selected cell (whole layer when nothing is selected). */
  fillSelection(char?: string): void;
  /** Applies the brush gradient across the selection without dragging. */
  applyGradientToSelection(): void;
  createGradientPulse(anim?: Partial<GradientAnimation>, frames?: number): void;
  /** Strips colours and attributes, keeping the characters. */
  clearSelectionColors(): void;
  /** Recolours the selection without touching its characters. */
  fillSelectionColor(target: 'fg' | 'bg' | 'both'): void;
  insertOrigin(): Point;

  writeCells(writes: CellWrite[], label: string): void;
  setCaret(point: Point | null): void;
  typeText(text: string): void;
  caretBackspace(): void;
  caretNewline(): void;

  newDocument(opts: { name: string; width: number; height: number; colorMode: ColorMode; background: Color }): void;
  openTab(tab: EditorTab): void;
  closeTab(id: string): void;
  setActiveTab(id: string): void;
  resizeDocument(width: number, height: number, anchor?: 'topleft' | 'center'): void;
  renameDocument(name: string): void;
  setColorMode(mode: ColorMode): void;
  setBackground(color: Color): void;

  importAnsi(text: string, fileName: string, opts?: { crop: boolean; width?: number }): void;
  loadProject(text: string, fileName: string): void;
  exportAnsi(options?: Partial<EncodeOptions>): string;
  projectJson(pretty?: boolean): string;

  setVariables(variables: VariableDef[]): void;
  upsertVariable(def: VariableDef, previousName?: string): void;
  removeVariable(name: string): void;
  setPreviewValue(name: string, value: string | number | boolean | null): void;
  toggleVariablePreview(): void;
  variableRegistry(): VariableRegistry;
  resolvedValues(): VariableValues;

  saveComponent(name: string): void;
  stampComponent(id: string, at: Point): void;
  removeComponent(id: string): void;

  addGuide(axis: 'x' | 'y', position: number): void;
  clearGuides(): void;

  /* animation */
  createAnimation(): void;
  removeAnimation(id: string): void;
  updateAnimation(id: string, patch: Partial<Animation>): void;
  setActiveAnimation(id: string | null): void;
  /** Captures the current layer visibility as a new frame. */
  captureFrame(): void;
  /** Adds a frame backed by a brand-new layer, for drawing frame by frame. */
  addFrameWithLayer(): void;
  duplicateFrame(frameId: string): void;
  removeFrame(frameId: string): void;
  moveFrame(frameId: string, toIndex: number): void;
  renameFrame(frameId: string, name: string): void;
  setFrameDuration(frameId: string, durationMs: number | undefined): void;
  toggleFrameLayer(frameId: string, layerId: string): void;
  /** Shows a frame on the canvas by applying its layer visibility. */
  gotoFrame(index: number): void;
  playAnimation(): void;
  pauseAnimation(): void;
  stepFrame(delta: number): void;
}

let tabCounter = 0;
let messageCounter = 0;

function makeTab(doc: TuiDocument, fileName: string): EditorTab {
  tabCounter += 1;
  return {
    id: `tab-${tabCounter}`,
    doc,
    history: new History(250),
    selection: new Selection(doc.width, doc.height),
    fileName,
    dirty: false,
    components: [],
    animations: [],
    createdAt: new Date().toISOString(),
  };
}

function emptyStroke(point: Point, frac: Point, mods: { shift: boolean; alt: boolean; ctrl: boolean }, button: number): ToolStroke {
  return {
    start: point,
    current: point,
    points: [point],
    startFrac: frac,
    currentFrac: frac,
    fracPoints: [{ x: point.x, y: point.y, fx: frac.x, fy: frac.y }],
    shift: mods.shift,
    alt: mods.alt,
    ctrl: mods.ctrl,
    button,
  };
}

export const useEditor = create<EditorState>((set, get) => {
  const initialDoc = new TuiDocument({ name: 'Untitled', width: 100, height: 30 });
  const firstTab = makeTab(initialDoc, 'untitled.tui.json');

  /** Builds the environment tools run against. */
  const toolEnv = (): ToolEnv => {
    const tab = get().active();
    const layer = tab.doc.activeLayer;
    const composite = tab.doc.composite();
    return {
      doc: tab.doc,
      layer,
      selection: tab.selection,
      brush: get().brush,
      read: (x, y) => layer.grid.get(x, y),
      readComposite: (x, y) => composite.get(x, y),
    };
  };

  /**
   * Applies a batch of cell writes as one undo step.
   *
   * Returns *why* nothing happened when nothing did. A freehand stroke that
   * repaints an identical cell should stay quiet, but a button press that
   * silently does nothing is the worst kind of bug to be on the receiving end
   * of — so the deliberate actions below turn these codes into an explanation.
   */
  const commit = (writes: CellWrite[], label: string): CommitResult => {
    const tab = get().active();
    const layer = tab.doc.activeLayer;
    if (layer.locked) {
      get().notify(`Layer "${layer.name}" is locked.`, 'warn');
      return 'locked';
    }
    /*
     * A hidden layer accepts writes perfectly well and shows none of them, so
     * the edit looks like it failed while quietly succeeding somewhere the user
     * cannot see. Refusing is the honest outcome — the same treatment a locked
     * layer gets.
     */
    if (!layer.visible) {
      get().notify(`Layer "${layer.name}" is hidden, so nothing would show. Show it first.`, 'warn');
      return 'hidden';
    }
    if (writes.length === 0) return 'nothing-to-write';
    const patch = new CellPatch(label);
    for (const w of writes) patch.write(layer, w.x, w.y, w.cell);
    const command = patch.toCommand();
    if (!command) return 'no-change';
    tab.history.push(command);
    tab.dirty = true;
    tab.doc.touch();
    set({ revision: get().revision + 1, historyState: tab.history.state });
    return 'ok';
  };

  return {
    tabs: [firstTab],
    activeTabId: firstTab.id,
    revision: 0,
    historyState: firstTab.history.state,

    tool: 'pencil',
    previousTool: null,
    brush: { ...DEFAULT_BRUSH },
    renderOptions: { ...DEFAULT_RENDER_OPTIONS },

    drag: null,
    preview: null,
    marquee: null,
    cursor: null,
    caret: null,

    clipboard: null,
    recentColors: [0x32ff94, 0x4477ff, 0xff6b6b, 0xffd166, 0xc8ccd4, 0x06070a],
    messages: [],
    showVariablePreview: true,

    activeAnimationId: null,
    playing: false,
    currentFrameIndex: 0,

    active() {
      const s = get();
      return s.tabs.find((t) => t.id === s.activeTabId) ?? s.tabs[0];
    },

    bump() {
      const tab = get().active();
      tab.doc.touch();
      set({ revision: get().revision + 1 });
    },

    notify(text, kind = 'info') {
      messageCounter += 1;
      const msg: StatusMessage = { id: messageCounter, text, kind };
      set({ messages: [...get().messages, msg].slice(-4) });
      const id = msg.id;
      setTimeout(() => get().dismiss(id), kind === 'error' ? 8000 : 4000);
    },

    dismiss(id) {
      set({ messages: get().messages.filter((m) => m.id !== id) });
    },

    setTool(tool) {
      const current = get().tool;
      if (current === tool) return;
      set({ tool, previousTool: current, preview: null, marquee: null, caret: tool === 'text' ? get().caret : null });
    },

    setBrush(patch) {
      set({ brush: { ...get().brush, ...patch } });
      if (patch.fg !== undefined) get().pushRecentColor(patch.fg);
      if (patch.bg !== undefined) get().pushRecentColor(patch.bg);
    },

    setRenderOptions(patch) {
      set({ renderOptions: { ...get().renderOptions, ...patch } });
    },

    pushRecentColor(color) {
      if (color === TRANSPARENT) return;
      const next = [color, ...get().recentColors.filter((c) => c !== color)].slice(0, 18);
      set({ recentColors: next });
    },

    beginStroke(point, frac, mods, button) {
      const state = get();
      // Dragging from inside an existing selection moves it, the way every
      // design tool behaves — no need to switch to the Move tool first.
      const tab0 = state.active();
      const dragsSelection =
        state.tool === 'select' &&
        !mods.shift &&
        !mods.ctrl &&
        !tab0.selection.isEmpty &&
        tab0.selection.has(point.x, point.y);
      const activeToolId = dragsSelection ? 'move' : state.tool;
      const tool = getTool(activeToolId);
      const stroke = emptyStroke(point, frac, mods, button);

      if (tool.pick) {
        const picked = tool.pick(stroke, toolEnv());
        if (picked) {
          state.setBrush(picked);
          state.notify('Picked cell style.', 'success');
        }
        return;
      }
      if (tool.planSelection && tool.instant) {
        const result = tool.planSelection(stroke, toolEnv());
        if (result) applySelection(get, set, result.selection, result.mode);
        return;
      }
      if (tool.instant && tool.plan) {
        commit(tool.plan(stroke, toolEnv()), tool.name);
        return;
      }
      set({ drag: { active: true, tool: activeToolId, stroke } });
      state.updateStroke(point, frac, mods);
    },

    updateStroke(point, frac, mods) {
      const state = get();
      const drag = state.drag;
      if (!drag?.active) {
        set({ cursor: point });
        return;
      }
      const tool = getTool(drag.tool);
      const stroke = drag.stroke;
      stroke.current = point;
      stroke.currentFrac = frac;
      stroke.shift = mods.shift;
      stroke.alt = mods.alt;
      stroke.ctrl = mods.ctrl;

      const last = stroke.points[stroke.points.length - 1];
      if (!last || last.x !== point.x || last.y !== point.y) stroke.points.push(point);
      const lastFrac = stroke.fracPoints[stroke.fracPoints.length - 1];
      if (!lastFrac || lastFrac.x !== point.x || lastFrac.y !== point.y || lastFrac.fx !== frac.x || lastFrac.fy !== frac.y) {
        stroke.fracPoints.push({ x: point.x, y: point.y, fx: frac.x, fy: frac.y });
      }

      if (tool.planSelection) {
        set({ marquee: rectFromPoints(stroke.start, stroke.current), cursor: point });
        return;
      }
      if (!tool.plan) {
        set({ cursor: point });
        return;
      }
      const writes = tool.plan(stroke, toolEnv());
      const preview = new Map<number, Cell>();
      const width = state.active().doc.width;
      for (const w of writes) preview.set(w.y * width + w.x, w.cell);
      set({ preview, cursor: point, marquee: null });
    },

    endStroke() {
      const state = get();
      const drag = state.drag;
      if (!drag?.active) return;
      const tool = getTool(drag.tool);
      const env = toolEnv();

      if (tool.planSelection) {
        const result = tool.planSelection(drag.stroke, env);
        if (result) applySelection(get, set, result.selection, result.mode);
        set({ drag: null, marquee: null, preview: null });
        return;
      }
      if (tool.plan) {
        // The delta is measured before the commit: moving with no selection
        // uses the layer's content bounds, which the commit itself changes.
        const delta = drag.tool === 'move' ? moveDelta(drag.stroke, env) : null;
        const writes = tool.plan(drag.stroke, env);
        commit(writes, tool.name);
        if (delta && (delta.dx !== 0 || delta.dy !== 0)) {
          const tab = get().active();
          if (!tab.selection.isEmpty) {
            tab.selection = tab.selection.translate(delta.dx, delta.dy);
          }
        }
      }
      set({ drag: null, preview: null, marquee: null });
    },

    cancelStroke() {
      set({ drag: null, preview: null, marquee: null });
    },

    setCursor(point) {
      set({ cursor: point });
    },

    undo() {
      const tab = get().active();
      const cmd = tab.history.undo(tab.doc);
      if (!cmd) return;
      tab.dirty = true;
      set({ revision: get().revision + 1, historyState: tab.history.state });
      get().notify(`Undo: ${cmd.label}`);
    },

    redo() {
      const tab = get().active();
      const cmd = tab.history.redo(tab.doc);
      if (!cmd) return;
      tab.dirty = true;
      set({ revision: get().revision + 1, historyState: tab.history.state });
      get().notify(`Redo: ${cmd.label}`);
    },

    addLayer() {
      const tab = get().active();
      const layer = new Layer(tab.doc.width, tab.doc.height, {
        name: `Layer ${tab.doc.layers.length + 1}`,
      });
      const index = tab.doc.layers.length;
      const previousActive = tab.doc.activeLayerId;
      tab.history.execute(
        tab.doc,
        makeCommand(
          'Add layer',
          (doc) => {
            doc.insertLayer(layer, index);
            doc.activeLayerId = layer.id;
          },
          (doc) => {
            doc.layers = doc.layers.filter((l) => l.id !== layer.id);
            doc.activeLayerId = previousActive;
          },
        ),
      );
      tab.dirty = true;
      set({ revision: get().revision + 1, historyState: tab.history.state });
    },

    removeLayer(id) {
      const tab = get().active();
      if (tab.doc.layers.length <= 1) {
        get().notify('A document needs at least one layer.', 'warn');
        return;
      }
      const index = tab.doc.layerIndex(id);
      const layer = tab.doc.layerById(id);
      if (!layer || index < 0) return;
      const previousActive = tab.doc.activeLayerId;
      tab.history.execute(
        tab.doc,
        makeCommand(
          `Delete layer "${layer.name}"`,
          (doc) => {
            doc.removeLayer(id);
          },
          (doc) => {
            doc.insertLayer(layer, index);
            doc.activeLayerId = previousActive;
          },
        ),
      );
      tab.dirty = true;
      set({ revision: get().revision + 1, historyState: tab.history.state });
    },

    duplicateLayer(id) {
      const tab = get().active();
      const src = tab.doc.layerById(id);
      if (!src) return;
      const copy = src.clone(true);
      copy.name = `${src.name} copy`;
      const index = tab.doc.layerIndex(id) + 1;
      const previousActive = tab.doc.activeLayerId;
      tab.history.execute(
        tab.doc,
        makeCommand(
          'Duplicate layer',
          (doc) => {
            doc.insertLayer(copy, index);
            doc.activeLayerId = copy.id;
          },
          (doc) => {
            doc.layers = doc.layers.filter((l) => l.id !== copy.id);
            doc.activeLayerId = previousActive;
          },
        ),
      );
      tab.dirty = true;
      set({ revision: get().revision + 1, historyState: tab.history.state });
    },

    renameLayer(id, name) {
      const tab = get().active();
      const layer = tab.doc.layerById(id);
      if (!layer) return;
      const before = layer.name;
      tab.history.execute(
        tab.doc,
        makeCommand(
          'Rename layer',
          (doc) => {
            const l = doc.layerById(id);
            if (l) l.name = name;
          },
          (doc) => {
            const l = doc.layerById(id);
            if (l) l.name = before;
          },
        ),
      );
      tab.dirty = true;
      set({ revision: get().revision + 1, historyState: tab.history.state });
    },

    toggleLayerVisible(id) {
      const tab = get().active();
      const layer = tab.doc.layerById(id);
      if (!layer) return;
      layer.visible = !layer.visible;
      tab.dirty = true;
      get().bump();
    },

    toggleLayerLocked(id) {
      const tab = get().active();
      const layer = tab.doc.layerById(id);
      if (!layer) return;
      layer.locked = !layer.locked;
      set({ revision: get().revision + 1 });
    },

    moveLayer(id, toIndex) {
      const tab = get().active();
      const from = tab.doc.layerIndex(id);
      if (from < 0) return;
      tab.history.execute(
        tab.doc,
        makeCommand(
          'Reorder layers',
          (doc) => doc.moveLayer(id, toIndex),
          (doc) => doc.moveLayer(id, from),
        ),
      );
      tab.dirty = true;
      set({ revision: get().revision + 1, historyState: tab.history.state });
    },

    setActiveLayer(id) {
      const tab = get().active();
      if (!tab.doc.layerById(id)) return;
      tab.doc.activeLayerId = id;
      set({ revision: get().revision + 1 });
    },

    mergeLayerDown(id) {
      const tab = get().active();
      const index = tab.doc.layerIndex(id);
      if (index <= 0) {
        get().notify('Nothing below to merge into.', 'warn');
        return;
      }
      const upper = tab.doc.layers[index];
      const lower = tab.doc.layers[index - 1];
      const lowerBefore = lower.grid.clone();
      tab.history.execute(
        tab.doc,
        makeCommand(
          'Merge layer down',
          (doc) => {
            const l = doc.layers[index - 1];
            l.grid.composite(upper.grid);
            doc.removeLayer(upper.id);
            doc.activeLayerId = l.id;
          },
          (doc) => {
            const l = doc.layerById(lower.id);
            if (l) l.grid = lowerBefore.clone();
            doc.insertLayer(upper, index);
            doc.activeLayerId = upper.id;
          },
        ),
      );
      tab.dirty = true;
      set({ revision: get().revision + 1, historyState: tab.history.state });
    },

    selectAll() {
      const tab = get().active();
      tab.selection.selectAll();
      set({ revision: get().revision + 1 });
    },

    selectNone() {
      const tab = get().active();
      tab.selection.clear();
      set({ revision: get().revision + 1 });
    },

    invertSelection() {
      const tab = get().active();
      tab.selection.invert();
      set({ revision: get().revision + 1 });
    },

    selectByColor(color, channel) {
      const tab = get().active();
      const grid = tab.doc.composite();
      tab.selection = Selection.fromPredicate(grid, (c) => (channel === 'fg' ? c.fg : c.bg) === color);
      set({ revision: get().revision + 1 });
      get().notify(`Selected ${tab.selection.size} cells.`);
    },

    selectByChar(char) {
      const tab = get().active();
      const grid = tab.doc.composite();
      tab.selection = Selection.fromPredicate(grid, (c) => c.char === char);
      set({ revision: get().revision + 1 });
      get().notify(`Selected ${tab.selection.size} cells.`);
    },

    setSelection(selection) {
      const tab = get().active();
      tab.selection = selection;
      set({ revision: get().revision + 1 });
    },

    copySelection() {
      const tab = get().active();
      const bounds = tab.selection.getBounds() ?? {
        x: 0,
        y: 0,
        width: tab.doc.width,
        height: tab.doc.height,
      };
      const source = tab.doc.composite();
      const clip = new Grid(bounds.width, bounds.height);
      for (let y = 0; y < bounds.height; y++) {
        for (let x = 0; x < bounds.width; x++) {
          const sx = bounds.x + x;
          const sy = bounds.y + y;
          if (!tab.selection.allows(sx, sy)) continue;
          clip.set(x, y, source.get(sx, sy));
        }
      }
      set({ clipboard: clip });
      get().notify(`Copied ${bounds.width}×${bounds.height} cells.`, 'success');
    },

    cutSelection() {
      get().copySelection();
      get().deleteSelection();
    },

    paste(at) {
      const state = get();
      const clip = state.clipboard;
      if (!clip) {
        state.notify('Clipboard is empty.', 'warn');
        return;
      }
      const tab = state.active();
      const origin = at ?? tab.selection.getBounds() ?? { x: 0, y: 0 };
      const writes: CellWrite[] = [];
      for (let y = 0; y < clip.height; y++) {
        for (let x = 0; x < clip.width; x++) {
          const cell = clip.get(x, y);
          if (cell.char === '' && cell.bg === TRANSPARENT && cell.fg === TRANSPARENT) continue;
          writes.push({ x: origin.x + x, y: origin.y + y, cell });
        }
      }
      commit(writes, 'Paste');
    },

    deleteSelection() {
      const tab = get().active();
      const bounds = tab.selection.getBounds();
      if (!bounds) {
        get().notify('Nothing selected.', 'warn');
        return;
      }
      const writes: CellWrite[] = [];
      tab.selection.forEach((x, y) => writes.push({ x, y, cell: EMPTY_CELL }));
      const result = commit(writes, 'Delete');
      if (result === 'no-change' || result === 'nothing-to-write') {
        get().notify('Those cells are already empty on this layer.', 'warn');
      }
    },

    flipSelection(axis) {
      const tab = get().active();
      const bounds = tab.selection.getBounds() ?? {
        x: 0,
        y: 0,
        width: tab.doc.width,
        height: tab.doc.height,
      };
      const region = tab.doc.activeLayer.grid.extract(bounds);
      const flipped = axis === 'h' ? region.flipHorizontal() : region.flipVertical();
      const writes: CellWrite[] = [];
      for (let y = 0; y < bounds.height; y++) {
        for (let x = 0; x < bounds.width; x++) {
          writes.push({ x: bounds.x + x, y: bounds.y + y, cell: flipped.get(x, y) });
        }
      }
      commit(writes, axis === 'h' ? 'Flip horizontal' : 'Flip vertical');
    },

    replaceInSelection(match, replacement) {
      const tab = get().active();
      const layer = tab.doc.activeLayer;
      const writes: CellWrite[] = [];
      for (let y = 0; y < tab.doc.height; y++) {
        for (let x = 0; x < tab.doc.width; x++) {
          if (!tab.selection.allows(x, y)) continue;
          const cell = layer.grid.get(x, y);
          if (match.char !== undefined && cell.char !== match.char) continue;
          if (match.fg !== undefined && cell.fg !== match.fg) continue;
          if (match.bg !== undefined && cell.bg !== match.bg) continue;
          writes.push({ x, y, cell: { ...cell, ...replacement } });
        }
      }
      commit(writes, 'Replace');
      return writes.length;
    },

    nudgeSelection(dx, dy) {
      const tab = get().active();
      if (tab.selection.isEmpty) return;
      const bounds = tab.selection.getBounds();
      if (!bounds) return;
      // Clamped like a drag, so nudging at the edge stops instead of shoving
      // cells off the document and losing them.
      const ndx = Math.max(-bounds.x, Math.min(tab.doc.width - (bounds.x + bounds.width), dx));
      const ndy = Math.max(-bounds.y, Math.min(tab.doc.height - (bounds.y + bounds.height), dy));
      if (ndx === 0 && ndy === 0) return;

      const layer = tab.doc.activeLayer;
      const writes: CellWrite[] = [];
      const moved = new Map<number, Cell>();
      tab.selection.forEach((x, y) => {
        const cell = layer.grid.get(x, y);
        if (cell.char === '' && cell.bg === TRANSPARENT && cell.fg === TRANSPARENT) return;
        moved.set((y + ndy) * tab.doc.width + (x + ndx), cell);
      });
      tab.selection.forEach((x, y) => writes.push({ x, y, cell: EMPTY_CELL }));
      for (const [key, cell] of moved) {
        const x = key % tab.doc.width;
        const y = (key - x) / tab.doc.width;
        writes.push({ x, y, cell });
      }
      commit(writes, 'Nudge');
      tab.selection = tab.selection.translate(ndx, ndy);
      set({ revision: get().revision + 1 });
    },

    fillSelection(char) {
      const state = get();
      const tab = state.active();
      const brush = state.brush;
      const area = tab.selection.getBounds() ?? {
        x: 0,
        y: 0,
        width: tab.doc.width,
        height: tab.doc.height,
      };
      const layer = tab.doc.activeLayer;
      const writes: CellWrite[] = [];
      for (let y = area.y; y < area.y + area.height; y++) {
        for (let x = area.x; x < area.x + area.width; x++) {
          if (!tab.selection.allows(x, y)) continue;
          const existing = layer.grid.get(x, y);
          writes.push({
            x,
            y,
            cell: {
              char: brush.paintChar ? char ?? brush.char : existing.char,
              fg: brush.paintFg ? brush.fg : existing.fg,
              bg: brush.paintBg ? brush.bg : existing.bg,
              attrs: brush.attrs,
            },
          });
        }
      }
      if (commit(writes, 'Fill selection') === 'no-change') {
        get().notify(`Those cells already hold "${char ?? brush.char}" in this colour.`, 'warn');
      }
    },

    applyGradientToSelection() {
      const state = get();
      const tab = state.active();
      const def = state.brush.gradient;
      const area = tab.selection.getBounds() ?? {
        x: 0,
        y: 0,
        width: tab.doc.width,
        height: tab.doc.height,
      };
      // Without a drag, the ramp runs corner to corner across the selection.
      const from = { x: area.x, y: area.y };
      const to = { x: area.x + area.width - 1, y: area.y + area.height - 1 };
      const layer = tab.doc.activeLayer;
      const writes: CellWrite[] = [];
      // A foreground gradient colours characters, so it has nothing to act on
      // where there are none. Counted so the action can say so rather than
      // appearing to do nothing.
      let skippedEmpty = 0;
      for (let y = area.y; y < area.y + area.height; y++) {
        for (let x = area.x; x < area.x + area.width; x++) {
          if (!tab.selection.allows(x, y)) continue;
          const existing = layer.grid.get(x, y);
          if (existing.char === '' && def.target === 'fg') {
            skippedEmpty += 1;
            continue;
          }
          const color = sampleGradient(def, x, y, from, to);
          writes.push({
            x,
            y,
            cell: {
              char: existing.char === '' && def.target !== 'fg' ? ' ' : existing.char,
              fg: def.target === 'fg' || def.target === 'both' ? color : existing.fg,
              bg: def.target === 'bg' || def.target === 'both' ? color : existing.bg,
              attrs: existing.attrs,
            },
          });
        }
      }
      const result = commit(writes, 'Apply gradient');
      if (result === 'nothing-to-write' && skippedEmpty > 0) {
        get().notify(
          'This gradient is set to colour text, and these cells are empty. Set “Applies to” to Background, or fill with a character first.',
          'warn',
        );
      } else if (result === 'no-change') {
        get().notify('The selection already carries this gradient.', 'warn');
      }
    },

    /**
     * Bakes a moving gradient into animation frames.
     *
     * The gradient maths already knows how to shift a ramp over time, but a
     * frame in this editor is a *set of visible layers* rather than a snapshot,
     * so a moving gradient has to become layers to exist at all. Each frame
     * gets one layer holding the selected characters recoloured at that point
     * in the cycle; the artwork itself is never touched, so the pulse can be
     * deleted afterwards and leave the design as it was.
     *
     * Building it this way means it plays everywhere the animation system
     * already reaches — the timeline, the ANSI export, the runtime player and
     * the SSH server — instead of being a preview that only the editor knows
     * how to draw.
     */
    createGradientPulse(patch = {}, frames = 12) {
      const state = get();
      const tab = state.active();
      const def = state.brush.gradient;
      const count = Math.max(2, Math.min(60, Math.floor(frames)));

      const source = tab.doc.activeLayer;
      if (source.locked) {
        get().notify(`Layer "${source.name}" is locked.`, 'warn');
        return;
      }

      const area = tab.selection.getBounds() ?? {
        x: 0,
        y: 0,
        width: tab.doc.width,
        height: tab.doc.height,
      };

      // The ramp runs across the selection, so the colours travel the width of
      // what was selected rather than the width of the whole document.
      const from = { x: area.x, y: area.y };
      const to = { x: area.x + area.width - 1, y: area.y + area.height - 1 };

      // Only cells with a character take part: recolouring blank space would
      // paint a solid rectangle over the design.
      const targets: Array<{ x: number; y: number; cell: Cell }> = [];
      for (let y = area.y; y < area.y + area.height; y++) {
        for (let x = area.x; x < area.x + area.width; x++) {
          if (!tab.selection.allows(x, y)) continue;
          const cell = source.grid.get(x, y);
          if (cell.char === '' || cell.char === ' ') continue;
          targets.push({ x, y, cell });
        }
      }
      if (targets.length === 0) {
        get().notify(
          'Nothing to pulse — select some characters first. A gradient needs something to colour.',
          'warn',
        );
        return;
      }

      const anim: GradientAnimation = { ...DEFAULT_ANIMATION, ...patch, enabled: true };
      const offsets = offsetCycle(anim, count);

      const layers = offsets.map((offset, i) => {
        const layer = new Layer(tab.doc.width, tab.doc.height, {
          name: `Pulse ${i + 1}`,
          visible: i === 0,
        });
        for (const t of targets) {
          const color = sampleGradient({ ...def, offset }, t.x, t.y, from, to);
          layer.grid.set(t.x, t.y, {
            char: t.cell.char,
            fg: def.target === 'bg' ? t.cell.fg : color,
            bg: def.target === 'fg' ? t.cell.bg : color,
            attrs: t.cell.attrs,
          });
        }
        return layer;
      });

      // Everything visible now becomes the shared backdrop every frame shows,
      // minus the pulse layers themselves.
      const backdrop = tab.doc.layers.filter((l) => l.visible).map((l) => l.id);
      const animation = newAnimation(`Pulse ${tab.animations.length + 1}`);
      animation.fps = Math.max(1, Math.min(60, Math.round(anim.fps)));
      animation.loop = true;
      animation.frames = layers.map((layer, i) =>
        newFrame([...backdrop, layer.id], `Frame ${i + 1}`),
      );

      const insertAt = tab.doc.layers.length;
      tab.history.execute(
        tab.doc,
        makeCommand(
          'Gradient pulse',
          (doc) => {
            layers.forEach((layer, i) => doc.insertLayer(layer, insertAt + i));
          },
          (doc) => {
            const ids = new Set(layers.map((l) => l.id));
            doc.layers = doc.layers.filter((l) => !ids.has(l.id));
            if (ids.has(doc.activeLayerId)) doc.activeLayerId = doc.layers[0]?.id ?? '';
          },
        ),
      );

      tab.animations = [...tab.animations, animation];
      tab.dirty = true;
      set({
        activeAnimationId: animation.id,
        currentFrameIndex: 0,
        historyState: tab.history.state,
        revision: get().revision + 1,
      });
      get().gotoFrame(0);
      get().notify(
        `Built a ${count}-frame pulse over ${targets.length} cells at ${animation.fps} fps.`,
        'success',
      );
    },

    fillSelectionColor(target) {
      const state = get();
      const tab = state.active();
      const brush = state.brush;
      const layer = tab.doc.activeLayer;
      const area = tab.selection.getBounds() ?? {
        x: 0,
        y: 0,
        width: tab.doc.width,
        height: tab.doc.height,
      };
      const writes: CellWrite[] = [];
      for (let y = area.y; y < area.y + area.height; y++) {
        for (let x = area.x; x < area.x + area.width; x++) {
          if (!tab.selection.allows(x, y)) continue;
          const existing = layer.grid.get(x, y);
          // A background needs something to paint on, so an empty cell becomes
          // a space; the foreground only matters where a character already is.
          const paintsBg = target === 'bg' || target === 'both';
          if (existing.char === '' && !paintsBg) continue;
          writes.push({
            x,
            y,
            cell: {
              char: existing.char === '' && paintsBg ? ' ' : existing.char,
              fg: target === 'fg' || target === 'both' ? brush.fg : existing.fg,
              bg: paintsBg ? brush.bg : existing.bg,
              attrs: existing.attrs,
            },
          });
        }
      }
      const result = commit(writes, 'Fill colour');
      if (result === 'nothing-to-write' && target === 'fg') {
        get().notify(
          'There are no characters here to recolour. Use “Fill background”, or fill with a character first.',
          'warn',
        );
      } else if (result === 'no-change') {
        get().notify('The selection already uses this colour.', 'warn');
      }
    },

    clearSelectionColors() {
      const state = get();
      const tab = state.active();
      const layer = tab.doc.activeLayer;
      const area = tab.selection.getBounds() ?? {
        x: 0,
        y: 0,
        width: tab.doc.width,
        height: tab.doc.height,
      };
      const writes: CellWrite[] = [];
      for (let y = area.y; y < area.y + area.height; y++) {
        for (let x = area.x; x < area.x + area.width; x++) {
          if (!tab.selection.allows(x, y)) continue;
          const existing = layer.grid.get(x, y);
          if (existing.fg === TRANSPARENT && existing.bg === TRANSPARENT && existing.attrs === 0) continue;
          writes.push({ x, y, cell: { char: existing.char, fg: TRANSPARENT, bg: TRANSPARENT, attrs: 0 } });
        }
      }
      // 'locked' and 'hidden' have already explained themselves.
      const result = commit(writes, 'Clear colours');
      if (result === 'nothing-to-write' || result === 'no-change') {
        get().notify('These cells already use the terminal default colours.', 'warn');
      }
    },

    /**
     * Where generated content lands.
     *
     * A selection is the most deliberate statement of "put it here" available,
     * so it wins: marking out a box and then generating text drops the text
     * into that box. Failing that, the text caret, then the last hovered cell,
     * then the top-left corner.
     */
    insertOrigin() {
      const state = get();
      const bounds = state.active().selection.getBounds();
      if (bounds) return { x: bounds.x, y: bounds.y };
      return state.caret ?? state.cursor ?? { x: 0, y: 0 };
    },

    writeCells(writes, label) {
      commit(writes, label);
    },

    setCaret(point) {
      set({ caret: point });
    },

    typeText(text) {
      const state = get();
      const caret = state.caret;
      if (!caret) return;
      const tab = state.active();
      const brush = state.brush;
      const writes: CellWrite[] = [];
      let x = caret.x;
      for (const ch of text) {
        if (x >= tab.doc.width) break;
        writes.push({
          x,
          y: caret.y,
          cell: { char: ch, fg: brush.fg, bg: brush.bg, attrs: brush.attrs },
        });
        x += 1;
      }
      commit(writes, 'Type');
      set({ caret: { x, y: caret.y } });
    },

    caretBackspace() {
      const state = get();
      const caret = state.caret;
      if (!caret || caret.x <= 0) return;
      const x = caret.x - 1;
      commit([{ x, y: caret.y, cell: EMPTY_CELL }], 'Backspace');
      set({ caret: { x, y: caret.y } });
    },

    caretNewline() {
      const state = get();
      const caret = state.caret;
      const drag = state.drag;
      if (!caret) return;
      const originX = drag?.stroke.start.x ?? caret.x;
      set({ caret: { x: originX, y: caret.y + 1 } });
    },

    newDocument(opts) {
      const doc = new TuiDocument(opts);
      const tab = makeTab(doc, `${opts.name.toLowerCase().replace(/\s+/g, '-')}.tui.json`);
      set({ tabs: [...get().tabs, tab], activeTabId: tab.id, revision: get().revision + 1 });
      get().notify(`Created ${opts.width}×${opts.height} document.`, 'success');
    },

    openTab(tab) {
      set({ tabs: [...get().tabs, tab], activeTabId: tab.id, revision: get().revision + 1 });
    },

    closeTab(id) {
      const state = get();
      if (state.tabs.length <= 1) {
        state.notify('At least one document stays open.', 'warn');
        return;
      }
      const tabs = state.tabs.filter((t) => t.id !== id);
      const activeTabId = state.activeTabId === id ? tabs[tabs.length - 1].id : state.activeTabId;
      set({ tabs, activeTabId, revision: state.revision + 1 });
    },

    setActiveTab(id) {
      if (!get().tabs.some((t) => t.id === id)) return;
      set({ activeTabId: id, preview: null, marquee: null, revision: get().revision + 1 });
    },

    resizeDocument(width, height, anchor = 'topleft') {
      const tab = get().active();
      const before = tab.doc.layers.map((l) => l.grid.clone());
      const beforeSize = { width: tab.doc.width, height: tab.doc.height };
      const offsetX = anchor === 'center' ? Math.floor((width - tab.doc.width) / 2) : 0;
      const offsetY = anchor === 'center' ? Math.floor((height - tab.doc.height) / 2) : 0;
      tab.history.execute(
        tab.doc,
        makeCommand(
          `Resize to ${width}×${height}`,
          (doc) => doc.resize(width, height, offsetX, offsetY),
          (doc) => {
            doc.width = beforeSize.width;
            doc.height = beforeSize.height;
            doc.layers.forEach((l, i) => {
              if (before[i]) l.grid = before[i].clone();
            });
          },
        ),
      );
      tab.selection = new Selection(width, height);
      tab.dirty = true;
      set({ revision: get().revision + 1, historyState: tab.history.state });
    },

    renameDocument(name) {
      const tab = get().active();
      tab.doc.name = name;
      tab.dirty = true;
      set({ revision: get().revision + 1 });
    },

    setColorMode(mode) {
      const tab = get().active();
      tab.doc.colorMode = mode;
      tab.dirty = true;
      set({ revision: get().revision + 1, renderOptions: { ...get().renderOptions, previewMode: mode } });
    },

    setBackground(color) {
      const tab = get().active();
      tab.doc.background = color;
      tab.dirty = true;
      set({
        revision: get().revision + 1,
        renderOptions: { ...get().renderOptions, background: color },
      });
    },

    importAnsi(text, fileName, opts = { crop: true }) {
      const result = parseAnsi(text, opts.width ? { width: opts.width } : {});
      let grid = result.grid;
      if (opts.crop) {
        const bounds = grid.contentBounds();
        if (bounds) grid = grid.extract(bounds);
      }
      const doc = new TuiDocument({
        name: fileName.replace(/\.[^.]+$/, ''),
        width: grid.width,
        height: grid.height,
      });
      doc.layers[0].grid = grid;
      doc.layers[0].name = 'Imported';
      const tab = makeTab(doc, fileName);
      set({ tabs: [...get().tabs, tab], activeTabId: tab.id, revision: get().revision + 1 });
      get().notify(
        `Imported ${grid.width}×${grid.height} from ${fileName}.` +
          (result.warnings.length ? ` ${result.warnings.length} warning(s).` : ''),
        result.warnings.length ? 'warn' : 'success',
      );
      result.warnings.slice(0, 3).forEach((w) => get().notify(w, 'warn'));
    },

    loadProject(text, fileName) {
      try {
        const loaded = fromJson(text);
        const tab = makeTab(loaded.document, fileName);
        tab.components = loaded.components;
        tab.createdAt = loaded.created;
        // Frames reference layer ids, so prune any the file no longer contains.
        const layerIds = loaded.document.layers.map((l) => l.id);
        tab.animations = loaded.animations.map((a) =>
          pruneFrames(
            {
              id: a.id,
              name: a.name,
              fps: a.fps,
              loop: a.loop,
              pingPong: (a as { pingPong?: boolean }).pingPong ?? false,
              frames: a.frames.map((f, i) => ({
                id: (f as { id?: string }).id ?? `frame-${i}`,
                name: (f as { name?: string }).name ?? `Frame ${i + 1}`,
                layers: f.layers,
                durationMs: f.durationMs,
              })),
            },
            layerIds,
          ),
        );
        set({ tabs: [...get().tabs, tab], activeTabId: tab.id, revision: get().revision + 1 });
        get().notify(`Opened ${loaded.document.name}.`, 'success');
        loaded.warnings.slice(0, 3).forEach((w) => get().notify(w, 'warn'));
      } catch (e) {
        get().notify(`Could not open project: ${(e as Error).message}`, 'error');
      }
    },

    exportAnsi(options = {}) {
      const tab = get().active();
      return encodeAnsi(tab.doc.composite(), {
        colorMode: tab.doc.colorMode,
        background: tab.doc.background,
        ...options,
      }).text;
    },

    projectJson(pretty = true) {
      const tab = get().active();
      return toJson(
        tab.doc,
        {
          components: tab.components,
          created: tab.createdAt,
          animations: tab.animations.map((a) => ({
            id: a.id,
            name: a.name,
            fps: a.fps,
            loop: a.loop,
            pingPong: a.pingPong,
            frames: a.frames.map((f) => ({
              id: f.id,
              name: f.name,
              layers: f.layers,
              durationMs: f.durationMs,
            })),
          })),
        },
        pretty,
      );
    },

    setVariables(variables) {
      const tab = get().active();
      tab.doc.variables = variables;
      tab.dirty = true;
      set({ revision: get().revision + 1 });
    },

    upsertVariable(def, previousName) {
      const tab = get().active();
      const list = [...tab.doc.variables];
      const key = previousName ?? def.name;
      const index = list.findIndex((v) => v.name === key);
      if (index >= 0) list[index] = def;
      else list.push(def);
      tab.doc.variables = list;
      tab.dirty = true;
      set({ revision: get().revision + 1 });
    },

    removeVariable(name) {
      const tab = get().active();
      tab.doc.variables = tab.doc.variables.filter((v) => v.name !== name);
      delete tab.doc.previewValues[name];
      tab.dirty = true;
      set({ revision: get().revision + 1 });
    },

    setPreviewValue(name, value) {
      const tab = get().active();
      tab.doc.previewValues = { ...tab.doc.previewValues, [name]: value };
      tab.dirty = true;
      set({ revision: get().revision + 1 });
    },

    toggleVariablePreview() {
      set({ showVariablePreview: !get().showVariablePreview });
    },

    variableRegistry() {
      return new VariableRegistry(get().active().doc.variables);
    },

    resolvedValues() {
      const tab = get().active();
      return new VariableRegistry(tab.doc.variables).resolveScope(tab.doc.previewValues, true);
    },

    saveComponent(name) {
      const state = get();
      const tab = state.active();
      const bounds = tab.selection.getBounds();
      if (!bounds) {
        state.notify('Select an area to save as a component.', 'warn');
        return;
      }
      const region = tab.doc.composite().extract(bounds);
      const component: SerializedComponent = {
        id: `component-${Date.now().toString(36)}`,
        name,
        width: region.width,
        height: region.height,
        runs: encodeComponentRuns((i) => region.getAt(i), region.length),
      };
      tab.components = [...tab.components, component];
      tab.dirty = true;
      set({ revision: state.revision + 1 });
      state.notify(`Saved component "${name}".`, 'success');
    },

    stampComponent(id, at) {
      const tab = get().active();
      const component = tab.components.find((c) => c.id === id);
      if (!component) return;
      const grid = new Grid(component.width, component.height);
      let i = 0;
      for (const run of component.runs) {
        const [count, char, fg, bg, attrs] = run;
        for (let n = 0; n < count && i < grid.length; n++, i++) {
          grid.setAt(i, { char, fg, bg, attrs });
        }
      }
      const writes: CellWrite[] = [];
      for (let y = 0; y < grid.height; y++) {
        for (let x = 0; x < grid.width; x++) {
          const cell = grid.get(x, y);
          if (cell.char === '' && cell.bg === TRANSPARENT && cell.fg === TRANSPARENT) continue;
          writes.push({ x: at.x + x, y: at.y + y, cell });
        }
      }
      commit(writes, `Insert ${component.name}`);
    },

    removeComponent(id) {
      const tab = get().active();
      tab.components = tab.components.filter((c) => c.id !== id);
      set({ revision: get().revision + 1 });
    },

    addGuide(axis, position) {
      const tab = get().active();
      tab.doc.guides = [...tab.doc.guides, { axis, position }];
      set({ revision: get().revision + 1 });
    },

    clearGuides() {
      const tab = get().active();
      tab.doc.guides = [];
      set({ revision: get().revision + 1 });
    },

    /* ---------------- animation ---------------- */

    createAnimation() {
      const tab = get().active();
      const animation = newAnimation(`Animation ${tab.animations.length + 1}`);
      // Seed it with the current view so there is something to play immediately.
      animation.frames.push(
        newFrame(
          tab.doc.layers.filter((l) => l.visible).map((l) => l.id),
          'Frame 1',
        ),
      );
      tab.animations = [...tab.animations, animation];
      tab.dirty = true;
      set({
        activeAnimationId: animation.id,
        currentFrameIndex: 0,
        revision: get().revision + 1,
      });
    },

    removeAnimation(id) {
      const state = get();
      const tab = state.active();
      tab.animations = tab.animations.filter((a) => a.id !== id);
      tab.dirty = true;
      set({
        activeAnimationId: state.activeAnimationId === id ? null : state.activeAnimationId,
        playing: false,
        revision: state.revision + 1,
      });
    },

    updateAnimation(id, patch) {
      const tab = get().active();
      tab.animations = tab.animations.map((a) => (a.id === id ? { ...a, ...patch } : a));
      tab.dirty = true;
      set({ revision: get().revision + 1 });
    },

    setActiveAnimation(id) {
      set({ activeAnimationId: id, playing: false, currentFrameIndex: 0 });
      if (id) get().gotoFrame(0);
    },

    captureFrame() {
      const state = get();
      const tab = state.active();
      const animation = tab.animations.find((a) => a.id === state.activeAnimationId);
      if (!animation) {
        state.notify('Create an animation first.', 'warn');
        return;
      }
      const frame = newFrame(
        tab.doc.layers.filter((l) => l.visible).map((l) => l.id),
        `Frame ${animation.frames.length + 1}`,
      );
      state.updateAnimation(animation.id, { frames: [...animation.frames, frame] });
      set({ currentFrameIndex: animation.frames.length });
      state.notify(`Captured ${frame.name} (${frame.layers.length} layers).`, 'success');
    },

    addFrameWithLayer() {
      const state = get();
      const tab = state.active();
      const animation = tab.animations.find((a) => a.id === state.activeAnimationId);
      if (!animation) {
        state.notify('Create an animation first.', 'warn');
        return;
      }
      const layer = new Layer(tab.doc.width, tab.doc.height, {
        name: `Frame ${animation.frames.length + 1}`,
      });
      const index = tab.doc.layers.length;
      tab.history.execute(
        tab.doc,
        makeCommand(
          'Add animation frame',
          (doc) => {
            doc.insertLayer(layer, index);
            doc.activeLayerId = layer.id;
          },
          (doc) => {
            doc.layers = doc.layers.filter((l) => l.id !== layer.id);
          },
        ),
      );
      // Shared layers (anything every existing frame includes) stay visible in
      // the new frame, so backgrounds and borders carry over automatically.
      const shared =
        animation.frames.length > 0
          ? animation.frames[0].layers.filter((id) =>
              animation.frames.every((f) => f.layers.includes(id)),
            )
          : [];
      const frame = newFrame([...shared, layer.id], `Frame ${animation.frames.length + 1}`);
      const frames = [...animation.frames, frame];
      tab.animations = tab.animations.map((a) => (a.id === animation.id ? { ...a, frames } : a));
      tab.dirty = true;
      set({ currentFrameIndex: frames.length - 1, revision: get().revision + 1 });
      get().gotoFrame(frames.length - 1);
    },

    duplicateFrame(frameId) {
      const state = get();
      const tab = state.active();
      const animation = tab.animations.find((a) => a.id === state.activeAnimationId);
      if (!animation) return;
      const index = animation.frames.findIndex((f) => f.id === frameId);
      if (index < 0) return;
      const source = animation.frames[index];
      const copy = newFrame(source.layers, `${source.name} copy`);
      copy.durationMs = source.durationMs;
      const frames = [...animation.frames];
      frames.splice(index + 1, 0, copy);
      state.updateAnimation(animation.id, { frames });
      set({ currentFrameIndex: index + 1 });
    },

    removeFrame(frameId) {
      const state = get();
      const tab = state.active();
      const animation = tab.animations.find((a) => a.id === state.activeAnimationId);
      if (!animation) return;
      const frames = animation.frames.filter((f) => f.id !== frameId);
      state.updateAnimation(animation.id, { frames });
      set({ currentFrameIndex: Math.max(0, Math.min(state.currentFrameIndex, frames.length - 1)) });
    },

    moveFrame(frameId, toIndex) {
      const state = get();
      const tab = state.active();
      const animation = tab.animations.find((a) => a.id === state.activeAnimationId);
      if (!animation) return;
      const from = animation.frames.findIndex((f) => f.id === frameId);
      if (from < 0) return;
      const frames = [...animation.frames];
      const [frame] = frames.splice(from, 1);
      frames.splice(Math.max(0, Math.min(frames.length, toIndex)), 0, frame);
      state.updateAnimation(animation.id, { frames });
    },

    renameFrame(frameId, name) {
      const state = get();
      const tab = state.active();
      const animation = tab.animations.find((a) => a.id === state.activeAnimationId);
      if (!animation) return;
      state.updateAnimation(animation.id, {
        frames: animation.frames.map((f) => (f.id === frameId ? { ...f, name } : f)),
      });
    },

    setFrameDuration(frameId, durationMs) {
      const state = get();
      const tab = state.active();
      const animation = tab.animations.find((a) => a.id === state.activeAnimationId);
      if (!animation) return;
      state.updateAnimation(animation.id, {
        frames: animation.frames.map((f) => (f.id === frameId ? { ...f, durationMs } : f)),
      });
    },

    toggleFrameLayer(frameId, layerId) {
      const state = get();
      const tab = state.active();
      const animation = tab.animations.find((a) => a.id === state.activeAnimationId);
      if (!animation) return;
      const frames = animation.frames.map((f) => {
        if (f.id !== frameId) return f;
        const has = f.layers.includes(layerId);
        return { ...f, layers: has ? f.layers.filter((id) => id !== layerId) : [...f.layers, layerId] };
      });
      state.updateAnimation(animation.id, { frames });
      const index = frames.findIndex((f) => f.id === frameId);
      if (index === state.currentFrameIndex) get().gotoFrame(index);
    },

    gotoFrame(index) {
      const state = get();
      const tab = state.active();
      const animation = tab.animations.find((a) => a.id === state.activeAnimationId);
      if (!animation) return;
      const order = playbackOrder(animation);
      if (order.length === 0) return;
      const clamped = ((index % order.length) + order.length) % order.length;
      const frame = order[clamped];
      // Playback drives the canvas through layer visibility, so what is on
      // screen during playback is exactly what the frame exports as.
      for (const layer of tab.doc.layers) layer.visible = frame.layers.includes(layer.id);

      /*
       * Editing follows the frame.
       *
       * Without this, stepping to a frame leaves the previous frame's layer
       * active — and that layer is now hidden. Every edit would land somewhere
       * invisible: drawing would appear to do nothing, and an action over the
       * artwork on screen would report empty cells, because the cells it was
       * actually looking at belong to a different layer. The topmost layer the
       * frame shows is what "here" means while that frame is up.
       */
      if (!frame.layers.includes(tab.doc.activeLayerId)) {
        const editable = [...tab.doc.layers]
          .reverse()
          .find((l) => frame.layers.includes(l.id) && !l.locked);
        if (editable) tab.doc.activeLayerId = editable.id;
      }

      tab.doc.touch();
      set({ currentFrameIndex: clamped, revision: get().revision + 1 });
    },

    playAnimation() {
      const state = get();
      const tab = state.active();
      const animation = tab.animations.find((a) => a.id === state.activeAnimationId);
      if (!animation || animation.frames.length === 0) {
        state.notify('Add at least one frame first.', 'warn');
        return;
      }
      set({ playing: true });
    },

    pauseAnimation() {
      set({ playing: false });
    },

    stepFrame(delta) {
      const state = get();
      state.gotoFrame(state.currentFrameIndex + delta);
    },
  };
});

function applySelection(
  get: () => EditorState,
  set: (patch: Partial<EditorState>) => void,
  selection: Selection,
  mode: 'replace' | 'add' | 'subtract',
): void {
  const tab = get().active();
  if (mode === 'replace') {
    tab.selection = selection;
  } else if (mode === 'add') {
    const merged = tab.selection.clone();
    selection.forEach((x, y) => merged.add(x, y));
    tab.selection = merged;
  } else {
    const merged = tab.selection.clone();
    selection.forEach((x, y) => merged.remove(x, y));
    tab.selection = merged;
  }
  set({ revision: get().revision + 1 });
}
